import { Fragment, memo, ReactElement, useCallback, ChangeEvent } from 'react';
import { Button, Switch } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import type { Weekday } from '../types';
import { WEEKDAYS, WEEKDAY_LABEL } from '../types';
import { useSchedule } from '../hooks/useSchedule';
import {
  ScheduleGrid,
  WeekdayLabelCell,
  TimeInput,
  ScheduleFooter,
  SectionHeading,
  ToggleSection,
  ToggleNote,
  ToggleRow,
  ToggleLabel,
} from './styles';
import TimeRangeSeparator from './TimeRangeSeparator';

interface Props {
  advertisementId: number;
}

const ScheduleEditor = memo(function ScheduleEditor({ advertisementId }: Props): ReactElement {
  const { current, baseline, saving, changeTime, toggleHoliday, toggleNewYearHolidays, save } =
    useSchedule(advertisementId);

  const handleChange = useCallback(
    (day: Weekday, edge: 'from' | 'to') => (e: ChangeEvent<HTMLInputElement>) => {
      changeTime(day, edge, e.target.value);
    },
    [changeTime]
  );

  return (
    <>
      <SectionHeading>自動架電対象の時間帯</SectionHeading>
      <ScheduleGrid>
        {WEEKDAYS.map(day => {
          const range = current.weekdays[day];
          const baselineRange = baseline.weekdays[day];
          return (
            <Fragment key={day}>
              <WeekdayLabelCell>{WEEKDAY_LABEL[day]}</WeekdayLabelCell>
              <TimeInput
                type='time'
                value={range.from ?? ''}
                onChange={handleChange(day, 'from')}
                aria-label={`${WEEKDAY_LABEL[day]} 開始時刻`}
                $dirty={range.from !== baselineRange.from}
              />
              <TimeRangeSeparator />
              <TimeInput
                type='time'
                value={range.to ?? ''}
                onChange={handleChange(day, 'to')}
                aria-label={`${WEEKDAY_LABEL[day]} 終了時刻`}
                $dirty={range.to !== baselineRange.to}
              />
            </Fragment>
          );
        })}
      </ScheduleGrid>
      <ToggleSection>
        <ToggleNote>祝日・年末年始に架電する場合の時間帯は、その日の曜日の設定に従います。</ToggleNote>
        <ToggleRow>
          <ToggleLabel>祝日に架電する</ToggleLabel>
          <Switch checked={current.callOnHoliday} onChange={toggleHoliday} aria-label='祝日に架電する' />
        </ToggleRow>
        <ToggleRow>
          <ToggleLabel>年末年始（12/29〜1/3）に架電する</ToggleLabel>
          <Switch
            checked={current.callOnNewYearHolidays}
            onChange={toggleNewYearHolidays}
            aria-label='年末年始に架電する'
          />
        </ToggleRow>
      </ToggleSection>
      <ScheduleFooter>
        <Button type='primary' icon={<SaveOutlined />} onClick={save} loading={saving}>
          保存
        </Button>
      </ScheduleFooter>
    </>
  );
});

export default ScheduleEditor;
